"use client";

import { useState } from "react";
import { Dialog, DialogHeader, DialogTitle, DialogBody } from "./ui/Dialog";
import { BotTranscript } from "./BotTranscript";
import { Badge } from "./ui/Badge";
import { TimeAgo } from "./TimeAgo";
import { useBotTraceForMessage, useBotTraceForPost } from "@/lib/hooks/useBotTrace";
import { useSignedIn } from "@/lib/hooks/useSignedIn";
import { isThetaError } from "@theta/api-client";
import { SignInToSee } from "./SignInToSee";

/**
 * ============================================================================
 * "HOW DID THE BOT GET TO THIS?" — the control next to a bot reply's AI badge
 * ============================================================================
 *
 * The companion to `BotBadge`, and the same argument: the badge says an AI
 * wrote this, and this says what it did to write it — the system prompt it was
 * given, the tools it called with which arguments, what came back, and the
 * answer it settled on. Every signed-in reader who can read the reply can
 * read this (signed-out is a 401 since OSO-695: a tool result can list the
 * group's members); see the route descriptions for that decision and what it
 * obliges a persona and a tool result to never contain.
 *
 * NOT LIVE, and it cannot be from here: the reply job runs in a separate
 * worker process and this platform has no push mechanism at all (no SSE, no
 * WebSocket, no LISTEN/NOTIFY). By the time a reply is on screen its
 * transcript is already written, which is why this reads as a document rather
 * than streaming.
 *
 * THE BOT ITSELF POINTS PEOPLE HERE. `backend/src/modules/llm-bot/persona.ts`
 * (`BASE_SYSTEM_PROMPT`, OSO-677) tells every bot that "?-ikonet ved siden af
 * dit navn" shows how a reply came to be — this control is that icon. Moving
 * or re-drawing it means updating that prompt line in the same change.
 *
 * THE CONTROL IS DRAWN FOR EVERY BOT REPLY, AND SOME HAVE NO TRANSCRIPT —
 * anything the bot posted before traces named their reply. The alternative is
 * a "has a transcript" flag threaded through `Post`, `Comment` and `Message`
 * and every reader that builds them, to hide a control on a handful of old
 * dev-build rows. So the fetch decides, and it is deferred until the button is
 * actually pressed: a feed of bot replies costs nothing until one is opened.
 */
export function BotTranscriptButton(
  props: ({ postId: number } | { messageId: number }) & { className?: string },
) {
  const [open, setOpen] = useState(false);
  const postId = "postId" in props ? props.postId : 0;
  const messageId = "messageId" in props ? props.messageId : 0;

  // The transcript is signed-in only (OSO-695), so a signed-out reader —
  // this dialog opens from a public event's discussion — is not even asked
  // for it: the 401 the ask would get is already known. Same gate the other
  // people-reads carry (`useSignedIn`).
  const signedIn = useSignedIn();
  // Both hooks are called unconditionally — rules of hooks — and the one that
  // is not this reply's kind stays disabled, so it never fetches.
  const postTrace = useBotTraceForPost(postId, open && postId > 0 && signedIn === true);
  const messageTrace = useBotTraceForMessage(
    messageId,
    open && messageId > 0 && signedIn === true,
  );
  const { data: trace, isLoading, isError, error } = postId > 0 ? postTrace : messageTrace;
  // The derived signed-out fact, plus the 401 itself for a lapsed session.
  const signedOut = signedIn === false || isThetaError(error, "unauthenticated");

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Se hvordan botten nåede frem til svaret"
        aria-label="Se hvordan botten nåede frem til svaret"
        className={`shrink-0 text-subtle-foreground hover:text-foreground transition-colors ${props.className ?? ""}`}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </button>

      {open && (
        <Dialog open onClose={() => setOpen(false)} className="max-w-2xl">
          <DialogHeader className="flex items-center justify-between gap-3">
            <DialogTitle>Sådan nåede botten frem til svaret</DialogTitle>
            {trace && (
              <div className="flex items-center gap-2 shrink-0">
                <Badge
                  tone={trace.succeeded ? "success" : "destructive"}
                  className="px-1.5 text-[10px] leading-none"
                >
                  {trace.succeeded ? "OK" : "FEJL"}
                </Badge>
                <TimeAgo date={trace.createdAt} />
              </div>
            )}
          </DialogHeader>

          <DialogBody>
            {/* `signedIn === undefined` is `/me` still settling: the query is
                gated off, so without this arm the dialog would flash "ingen
                transskription" before it could ask. */}
            {(isLoading || (signedIn === undefined && !signedOut)) && (
              <p className="text-sm text-muted-foreground">Indlæser...</p>
            )}

            {/* Signed out it is not a failure (OSO-695): a transcript can
                list the group's members, and people are login-only. */}
            {signedOut ? (
              <SignInToSee what="hvordan botten nåede frem til svaret" className="py-0 text-left" />
            ) : isError && (
              <p className="text-sm text-destructive-muted-foreground">
                Transskriptionen kunne ikke hentes. Prøv igen.
              </p>
            )}

            {signedIn === true && !isLoading && !isError && !trace && (
              <p className="text-sm text-muted-foreground">
                Der er ikke gemt nogen transskription for dette svar.
              </p>
            )}

            {trace && (
              <>
                {!trace.succeeded && (
                  <p className="mb-3 text-sm text-muted-foreground">
                    Botten kunne ikke svare og skrev sit standardsvar i stedet.
                    Transskriptionen nedenfor viser hvor langt den nåede.
                  </p>
                )}
                <BotTranscript transcript={trace.transcript} />
              </>
            )}
          </DialogBody>
        </Dialog>
      )}
    </>
  );
}
